import type { InsuranceVerdict } from '@/hooks/useInsuranceVerdicts'
import {
  IconCheck,
  IconExclamationMark,
  IconHistory,
  IconShieldFilled,
  type Icon,
} from '@tabler/icons-react'
import {
  APPOINTMENT_STATUS_CHIP,
  APPOINTMENT_STATUS_ICON,
  INSURANCE_STATUS_CHIP,
} from './scheduleCalendar.tokens'

export type StatusFilter = 'pending' | 'booked' | 'checked-in' | 'fulfilled' | 'noshow'
export type InsuranceFilter = 'all' | 'verified' | 'review-required'

type FilterableEvent = {
  id: string
  type?: string
  appointmentId?: string
  appointmentStatus?: string
}

type SegmentDef = {
  key: string
  label: string
  color: string
  count: number
  isTotal?: boolean
  pending?: boolean
  /** Status segments: the same glyph the card shows for this status. */
  Icon?: Icon
  /** Insurance segments: the card's filled shield with a check/exclamation inside. */
  shield?: 'check' | 'alert'
}

/** Matches the insurance shield rendered on appointment cards: a filled shield
 * (green Verified / amber Review required) with a white mark inside. */
function ShieldMark({ kind, color }: { kind: 'check' | 'alert'; color: string }) {
  const Mark = kind === 'check' ? IconCheck : IconExclamationMark
  return (
    <span className="relative inline-flex h-4 w-4 items-center justify-center">
      <IconShieldFilled size={16} color={color} className="absolute inset-0" />
      <Mark size={8} stroke={4} color="#fff" className="relative -mt-px" />
    </span>
  )
}

type Props = {
  events: FilterableEvent[]
  insuranceVerdictMap?: Record<string, InsuranceVerdict>
  returningPatientMap?: Record<string, boolean>
  insuranceFilterPending: boolean
  returningFilterPending: boolean
  /** Selected statuses; empty = show all (multiselect). */
  statusFilters: StatusFilter[]
  insuranceFilter: InsuranceFilter
  /** When on, only returning-patient encounters are shown. */
  returningFilter: boolean
  onStatusFiltersChange: (filters: StatusFilter[]) => void
  onInsuranceFilterChange: (filter: InsuranceFilter) => void
  onReturningFilterChange: (filter: boolean) => void
}

function SegmentBar({
  title,
  segments,
  isOn,
  onToggle,
}: {
  title: string
  segments: SegmentDef[]
  isOn: (key: string) => boolean
  onToggle: (key: string) => void
}) {
  return (
    <div className="schedule-v2-mulish inline-flex items-center gap-0.5 rounded-[9px] bg-[#F1F2F4] p-0.5">
      <span className="px-1.5 text-[10px] font-bold tracking-[.4px] text-[#9AA0A8] uppercase">
        {title}
      </span>
      {segments.map((segment) => {
        const on = isOn(segment.key)
        return (
          <button
            key={segment.key}
            type="button"
            title={`Show ${segment.label}`}
            aria-pressed={on}
            aria-busy={(on && segment.pending) || undefined}
            onClick={() => onToggle(segment.key)}
            className="inline-flex cursor-pointer items-center gap-1.5 rounded-[7px] border-none px-[11px] py-1 !text-[12px] !font-bold transition"
            style={{
              background: on ? '#fff' : 'transparent',
              color: on ? '#33383F' : '#9AA0A8',
              boxShadow: on ? '0 1px 2px rgba(16,24,40,.12)' : 'none',
            }}
          >
            {!segment.isTotal && segment.shield && (
              <ShieldMark kind={segment.shield} color={on ? segment.color : '#AEB4BC'} />
            )}
            {!segment.isTotal && segment.Icon && (
              <segment.Icon size={15} stroke={2.2} color={on ? segment.color : '#AEB4BC'} />
            )}
            {!segment.isTotal && !segment.shield && !segment.Icon && (
              <span
                className="h-2 w-2 rounded-full"
                style={{ background: segment.color, opacity: on ? 1 : 0.4 }}
              />
            )}
            <span>{segment.label}</span>
            <span
              className="inline-flex h-[15px] min-w-[15px] items-center justify-center rounded-full px-[5px] text-[9.5px] font-bold"
              style={{
                background: on ? `${segment.color}22` : '#EDEEF1',
                color: on ? segment.color : '#9AA0A8',
              }}
            >
              {segment.count}
            </span>
          </button>
        )
      })}
    </div>
  )
}

export function ScheduleFilterSegments({
  events,
  insuranceVerdictMap,
  returningPatientMap,
  insuranceFilterPending,
  returningFilterPending,
  statusFilters,
  insuranceFilter,
  returningFilter,
  onStatusFiltersChange,
  onInsuranceFilterChange,
  onReturningFilterChange,
}: Props) {
  // Every encounter on the page, including those with no appointment/status
  // saved — the "All" segment counts all of them, not just the ones with a
  // status. (Availability background bands are excluded.)
  const encounterEvents = events.filter((event) => event.type === 'appointment_slot')

  const statusCounts: Record<string, number> = {
    pending: 0,
    booked: 0,
    'checked-in': 0,
    fulfilled: 0,
    noshow: 0,
  }
  encounterEvents.forEach((event) => {
    const status = event.appointmentStatus
    if (status && statusCounts[status] != null) statusCounts[status]++
  })

  let verifiedCount = 0
  let reviewCount = 0
  encounterEvents.forEach((event) => {
    const status = insuranceVerdictMap?.[event.id]?.insuranceStatus
    if (status === 'verified') verifiedCount++
    if (status === 'review-required') reviewCount++
  })

  const returningCount = encounterEvents.filter((event) => returningPatientMap?.[event.id]).length

  const statusSegments: SegmentDef[] = [
    { key: 'all', label: 'All', color: '#6B7280', count: encounterEvents.length, isTotal: true },
    {
      key: 'pending',
      label: 'Pending confirmation',
      color: '#3B82F6',
      count: statusCounts.pending,
      Icon: APPOINTMENT_STATUS_ICON.pending,
    },
    {
      key: 'booked',
      label: 'Confirmed',
      color: '#16A34A',
      count: statusCounts.booked,
      Icon: APPOINTMENT_STATUS_ICON.booked,
    },
    {
      key: 'checked-in',
      label: APPOINTMENT_STATUS_CHIP['checked-in'].label,
      color: APPOINTMENT_STATUS_CHIP['checked-in'].color,
      count: statusCounts['checked-in'],
      Icon: APPOINTMENT_STATUS_ICON['checked-in'],
    },
    {
      key: 'fulfilled',
      label: 'Completed',
      color: '#94A3B8',
      count: statusCounts.fulfilled,
      Icon: APPOINTMENT_STATUS_ICON.fulfilled,
    },
    {
      key: 'noshow',
      label: 'No-show',
      color: '#EF4444',
      count: statusCounts.noshow,
      Icon: APPOINTMENT_STATUS_ICON.noshow,
    },
  ]

  const insuranceSegments: SegmentDef[] = [
    { key: 'all', label: 'All', color: '#6B7280', count: encounterEvents.length, isTotal: true },
    {
      key: 'verified',
      label: 'Verified',
      color: INSURANCE_STATUS_CHIP.verified.color,
      count: verifiedCount,
      shield: 'check',
      pending: insuranceFilterPending,
    },
    {
      key: 'review-required',
      label: 'Review required',
      color: INSURANCE_STATUS_CHIP['review-required'].color,
      count: reviewCount,
      shield: 'alert',
      pending: insuranceFilterPending,
    },
  ]

  // Same glyph the appointment card/popup shows for a returning patient.
  const returningSegments: SegmentDef[] = [
    {
      key: 'returning',
      label: 'Returning Patient',
      color: '#7A5AF8',
      count: returningCount,
      Icon: IconHistory,
      pending: returningFilterPending,
    },
  ]

  return (
    <div className="mt-4 flex flex-wrap items-center gap-3 gap-y-2.5 px-0.5">
      <SegmentBar
        title="Status"
        segments={statusSegments}
        isOn={(key) =>
          key === 'all' ? statusFilters.length === 0 : statusFilters.includes(key as StatusFilter)
        }
        onToggle={(key) => {
          if (key === 'all') {
            onStatusFiltersChange([])
            return
          }
          const status = key as StatusFilter
          onStatusFiltersChange(
            statusFilters.includes(status)
              ? statusFilters.filter((s) => s !== status)
              : [...statusFilters, status],
          )
        }}
      />
      <SegmentBar
        title="Patient"
        segments={returningSegments}
        isOn={() => returningFilter}
        onToggle={() => onReturningFilterChange(!returningFilter)}
      />
      <span className="ml-auto" />
      <SegmentBar
        title="Insurance"
        segments={insuranceSegments}
        isOn={(key) => insuranceFilter === key}
        onToggle={(key) =>
          onInsuranceFilterChange(insuranceFilter === key ? 'all' : (key as InsuranceFilter))
        }
      />
    </div>
  )
}
